import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import bbox from '@turf/bbox'
import { featureCollection, point } from '@turf/helpers'
import { renderToStaticMarkup } from 'react-dom/server'
import { useTrackerRuntime } from '../runtime'
import CarMarker from './Icons/CarIconMarker'
import HomeIconMarker from './Icons/HomeIconMarker'
import MarkerIconMerchant from './Icons/MerchantIcon'

const LEGACY_MAPBOX_TOKEN =
  'pk.eyJ1IjoiaGFiaXRhdG1pa2UiLCJhIjoiY2lsdHVpMWQxMDBjdnVza3NyNnV6Y3N3NCJ9.dWupCOy48vAo8BocejIdtg'

interface MapBoxProps {
  data: any
}

const MapBox = ({ data }: MapBoxProps) => {
  const runtime = useTrackerRuntime()
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markersRef = useRef<mapboxgl.Marker[]>([])

  const merchantLatitude = data?.merchant.location.coordinates[1]
  const merchantLongitude = data?.merchant.location.coordinates[0]
  const runnerLatitude = data?.runner?.location?.coordinates?.[1]
  const runnerLongitude = data?.runner?.location?.coordinates?.[0]
  const customerLatitude = data?.customer?.location?.coordinates?.[1]
  const customerLongitude = data?.customer?.location?.coordinates?.[0]

  const mapObj: any = {
    origin: {
      latitude: merchantLatitude,
      longitude: merchantLongitude,
    },
    destination: {
      latitude: customerLatitude,
      longitude: customerLongitude,
    },
    directions: [],
  }

  if (runnerLatitude && runnerLongitude) {
    mapObj.runner = {
      latitude: runnerLatitude,
      longitude: runnerLongitude,
    }
  }

  const finalBbox = buildBoundingBox(data.fulfillment?.status, mapObj)
  const showCompletedPhoto =
    !!data.order?.dropoff_photo && data.fulfillment?.status === 'completed'
  const mapboxToken = runtime.mapboxAccessToken || LEGACY_MAPBOX_TOKEN

  useEffect(() => {
    if (showCompletedPhoto || !mapContainerRef.current || mapRef.current) {
      return
    }

    mapboxgl.accessToken = mapboxToken

    const map = new mapboxgl.Map({
      accessToken: mapboxToken,
      attributionControl: false,
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11?optimize=true',
    })

    mapRef.current = map
    map.on('load', () => {
      map.resize()
    })

    return () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
      map.remove()
      mapRef.current = null
    }
  }, [mapboxToken, showCompletedPhoto])

  useEffect(() => {
    if (showCompletedPhoto || !mapRef.current) {
      return
    }

    const map = mapRef.current

    const updateMap = () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []

      const markers: mapboxgl.Marker[] = []

      if (
        !['completed', 'cancelled'].includes(data?.fulfillment?.status) &&
        runnerLatitude &&
        runnerLongitude
      ) {
        markers.push(
          new mapboxgl.Marker({
            element: createMarkerElement(
              <CarMarker width="28" height="28" status="black" />
            ),
          }).setLngLat([runnerLongitude, runnerLatitude])
        )
      }

      markers.push(
        new mapboxgl.Marker({
          anchor: 'center',
          element: createMarkerElement(
            <MarkerIconMerchant width="55" height="55" />
          ),
        }).setLngLat([merchantLongitude, merchantLatitude])
      )

      markers.push(
        new mapboxgl.Marker({
          anchor: 'center',
          element: createMarkerElement(<HomeIconMarker />),
        }).setLngLat([customerLongitude, customerLatitude])
      )

      markers.forEach(marker => marker.addTo(map))
      markersRef.current = markers

      if (finalBbox.length === 4) {
        map.fitBounds(
          [
            [finalBbox[0], finalBbox[1]],
            [finalBbox[2], finalBbox[3]],
          ],
          {
            padding: 75,
          }
        )
      }

      map.resize()
    }

    if (map.isStyleLoaded()) {
      updateMap()
      return
    }

    map.once('load', updateMap)
  }, [
    customerLatitude,
    customerLongitude,
    data,
    finalBbox,
    merchantLatitude,
    merchantLongitude,
    runnerLatitude,
    runnerLongitude,
    showCompletedPhoto,
  ])

  if (showCompletedPhoto) {
    return <img src={data.order?.dropoff_photo} alt="Delivery Completed" />
  }

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: '100%',
        height: '100%',
      }}
    />
  )
}

function createMarkerElement(jsx: JSX.Element) {
  const element = document.createElement('div')
  element.innerHTML = renderToStaticMarkup(jsx)

  return element.firstElementChild as HTMLElement
}

function buildBoundingBox(
  status: string,
  directions: {
    runner?: { latitude: number; longitude: number }
    directions: { latitude: number; longitude: number }[]
    origin: { latitude: number; longitude: number }
    destination: { latitude: number; longitude: number }
  }
): number[] {
  const bboxPoints = []

  try {
    if (directions.origin.longitude) {
      bboxPoints.push(point([directions.origin.longitude, directions.origin.latitude]))
    }

    if (directions.destination.longitude) {
      bboxPoints.push(
        point([directions.destination.longitude, directions.destination.latitude])
      )
    }

    if (status === 'in_progress' && directions.runner) {
      bboxPoints.push(point([directions.runner.longitude, directions.runner.latitude]))
    }

    const featureSet = featureCollection(bboxPoints)

    return bbox(featureSet)
  } catch (_error) {
    return []
  }
}

export default MapBox
